/**
 * @file CommonInput.tsx
 * @description
 * High-performance, premium input component. 
 * Fixed for Web to remove default browser outlines and internal textboxes.
 */

import React, { useState } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  TextInputProps,
  Platform,
  StyleProp,
  ViewStyle,
} from "react-native";
import { FontFamily } from "../theme/typography";
import { useTheme } from "../context/themeContext";
import type { ColorScheme } from "../theme/colors";

/* ================= TYPES ================= */

export type CommonInputProps = Readonly<{
  label?: string;
  value: string;
  placeholder?: string;
  error?: string;
  touched?: boolean;
  onChangeText?: (text: string) => void;
  isValid?: (value: string) => boolean;
  secureTextEntry?: boolean;
  rightIcon?: React.ReactNode;
  leftIcon?: React.ReactNode;
  disableCopyPaste?: boolean;
  inputProps?: TextInputProps;
  editable?: boolean;
  isDark?: boolean;
  isReadOnly?: boolean;
  badge?: string;
  /** Render the value in the mono face (numeric / code-like fields). */
  mono?: boolean;
  /** Show a danger-coloured asterisk after the label to mark a mandatory field. */
  required?: boolean;
  /**
   * When true (default) the error state surfaces as soon as the field is
   * blurred or has content. Set false to only surface it once `touched` is
   * explicitly set (e.g. on a submit attempt) — so merely clicking in and out
   * of an empty required field never leaves an error border behind.
   */
  validateOnBlur?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
}>;

/* ================= COMPONENT ================= */

/* ================= HELPER COMPONENTS ================= */

const InputLabel = ({ label, colors, isReadOnly, badge, required }: {
  label?: string;
  colors: ColorScheme;
  isReadOnly: boolean;
  badge?: string;
  required?: boolean;
}) => {
  if (!label) return null;
  return (
    <View style={styles.labelRow}>
      <Text style={[styles.label, { color: colors.ink3 }]}>
        {label}
        {required && <Text style={{ color: colors.danger, fontSize: 15, lineHeight: 12 }}> *</Text>}
      </Text>
      {(isReadOnly || badge) && (
        <View style={[styles.badgeWrapper, { backgroundColor: colors.paper2, borderColor: colors.line }]}>
          <Text style={[styles.badgeText, { color: colors.ink3 }]}>{badge ?? "SECURED"}</Text>
        </View>
      )}
    </View>
  );
};

/* ================= COMPONENT ================= */

export default function CommonInput({
  label,
  value,
  placeholder,
  error,
  touched = false,
  onChangeText,
  isValid = () => true,
  secureTextEntry = false,
  rightIcon,
  leftIcon,
  disableCopyPaste = false,
  inputProps = {},
  editable = true,
  isReadOnly = false,
  badge,
  mono = false,
  required = false,
  validateOnBlur = true,
  containerStyle,
}: CommonInputProps) {
  const { theme } = useTheme();
  const { colors } = theme;

  const [focused, setFocused] = useState(false);
  const [blurred, setBlurred] = useState(false);
  const { style: customStyle, ...restInputProps } = inputProps;

  const hasValue = value?.trim().length > 0;
  const isInputValid = isValid(value);
  // When validateOnBlur is off, the error surfaces ONLY on an explicit `touched`
  // (submit attempt) — clicking in/out of an empty field leaves no error border.
  const showError =
    (!isInputValid || !!error) && (touched || (validateOnBlur && (blurred || hasValue)));

  // Determine text color based on state
  let textColor = colors.ink;
  if (showError) textColor = colors.danger;

  const isWebPlatform = Platform.OS === 'web';
  const getWrapperStyles = () => {
    const baseWrapper = {
      backgroundColor: colors.card,
      borderColor: colors.line,
    };
    // Focus / error read as a crisp accent (or danger) border with a soft glow
    // ring on web — more elite than flooding the field background with colour.
    const activeBorder = {
      borderColor: colors.accent,
      ...(isWebPlatform ? ({ boxShadow: `0 0 0 3px ${colors.accentSoft}` } as any) : {}),
    };
    const errorBorder = {
      borderColor: colors.danger,
      ...(isWebPlatform ? ({ boxShadow: `0 0 0 3px ${colors.dangerSoft}` } as any) : {}),
    };
    const disabled = { backgroundColor: colors.paper2, borderColor: colors.line };
    return [
      styles.inputWrapper,
      baseWrapper,
      focused && activeBorder,
      showError && errorBorder,
      (isReadOnly || !editable) && [styles.disabledWrapper, disabled],
    ];
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <InputLabel label={label} colors={colors} isReadOnly={isReadOnly} badge={badge} required={required} />

      <View style={[getWrapperStyles(), rightIcon ? styles.wrapperWithRight : undefined]}>
        {leftIcon && <View style={styles.leftIconWrapper}>{leftIcon}</View>}

        <TextInput
          value={value}
          placeholder={placeholder}
          placeholderTextColor={colors.ink4}
          secureTextEntry={secureTextEntry}
          onChangeText={onChangeText}
          contextMenuHidden={disableCopyPaste}
          editable={editable && !isReadOnly}
          underlineColorAndroid="transparent"
          autoComplete="off"
          autoCorrect={false}
          {...(Platform.OS === 'web' ? { autoComplete: 'new-password' } as any : {})}
          style={[
            styles.input,
            {
              color: textColor,
              fontFamily: mono ? FontFamily.mono : FontFamily.sans,
            },
            Platform.OS === 'web' && { outlineStyle: 'none', borderWidth: 0 } as any,
            Platform.OS === 'android' && {
              fontFamily: "", // Forces system default
              fontWeight: undefined, // Fixes emoji fallback on some Android devices
            },
            customStyle,
          ]}

          {...restInputProps}
          onFocus={(e) => {
            setFocused(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setFocused(false);
            setBlurred(true);
            inputProps.onBlur?.(e);
          }}
        />
        {rightIcon && (
          <View style={styles.iconWrapper}>
            {rightIcon}
          </View>
        )}
      </View>
      {showError && !!error && (
        <Text style={[styles.errorText, { color: colors.danger }]}>{error}</Text>
      )}
    </View>
  );
}

/* ================= STYLES ================= */

const styles = StyleSheet.create({
  container: { marginBottom: 18, width: "100%" },
  label: {
    fontWeight: "700",
    fontSize: 10.5,
    fontFamily: FontFamily.sans,
    textTransform: "uppercase",
    letterSpacing: 1.2,
  },
  labelRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    paddingHorizontal: 2,
  },
  badgeWrapper: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 2,
    borderWidth: 1,
  },
  badgeText: {
    fontSize: 9,
    fontWeight: "900",
    letterSpacing: 1,
  },
  inputWrapper: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 38,
    borderWidth: 1.5,
    borderRadius: 2,
    paddingHorizontal: 12,
    paddingVertical: 4,
    // Clip long values so the text can't spill past the rounded border.
    overflow: "hidden",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.03, shadowRadius: 4,
      },
      android: { elevation: 1 },
      web: {
        transition: "border-color 0.15s ease, box-shadow 0.15s ease",
        boxShadow: "0 1px 2px 0 rgba(16, 24, 40, 0.04)",
      } as any,
    }),
  },
  input: {
    flex: 1,
    // minWidth:0 lets the flex child shrink below its content width — without
    // it, a long value (e.g. a long first name) pushes past the box on web.
    minWidth: 0,
    fontSize: 13,
    fontWeight: "600",
    fontFamily: FontFamily.sans,
    height: "100%",
    paddingVertical: 0,
    backgroundColor: "transparent",
  },
  disabledWrapper: {
    opacity: 0.7,
  },
  wrapperWithRight: { paddingRight: 56 },
  leftIconWrapper: { marginRight: 9, justifyContent: 'center', alignItems: 'center' },
  iconWrapper: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    width: 52,
  },
  errorText: {
    fontSize: 12,
    marginTop: 6,
    fontWeight: "600",
    marginLeft: 4,
  },
});
